// RoleGuard — declarative role-based UI visibility + route protection.
//
// Role hierarchy (low → high):
//   observer (1) < responder (2) < escalation_manager (3) < tenant_admin (4) < platform_admin (5)
//
// Usage:
//   <RoleGuard minRole="escalation_manager">       {/* hides if user role < min */}
//     <button>Escalate</button>
//   </RoleGuard>
//
//   <RoleGuard minRole="escalation_manager" fallback={<Pending />}>
//     <button>Approve</button>
//   </RoleGuard>
//
//   <RouteGuard minRole="responder">               {/* redirects to /incidents */}
//     <FMEAPage />
//   </RouteGuard>
//
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../store";
import { hasModuleAccess } from "../lib/moduleAccess";

export const ROLE_LEVELS = {
  observer: 1,
  responder: 2,
  escalation_manager: 3,
  tenant_admin: 4,
  platform_admin: 5,
};

export const roleLevel = (role) => ROLE_LEVELS[role] || 0;

export const hasMinRole = (userRole, minRole) =>
  roleLevel(userRole) >= roleLevel(minRole);

export const isObserver = (user) => user?.role === "observer";

// Conditional UI wrapper — renders children when user.role >= minRole; otherwise
// renders `fallback` (default: null = hidden completely).
export const RoleGuard = ({ minRole, fallback = null, children }) => {
  const { user } = useAuth();
  if (!user) return null;
  return hasMinRole(user.role, minRole) ? <>{children}</> : fallback;
};

// Route-level guard — redirects (default to /incidents) and fires a toast for
// observers attempting to access an off-limits route.
export const RouteGuard = ({ minRole, children, redirectTo = "/incidents" }) => {
  const { user } = useAuth();
  const addToast = useStore((s) => s.addToast);
  const location = useLocation();
  const denied = user && !hasMinRole(user.role, minRole);

  React.useEffect(() => {
    if (denied) {
      addToast({
        type: "info",
        title: "Read-only access",
        body: "Your role doesn't include this page.",
      });
    }
     
  }, [denied, location.pathname]);

  if (user === null) return null; // still authenticating
  if (denied) return <Navigate to={redirectTo} replace />;
  return children;
};

// Module-scoped guard — used in addition to RoleGuard. Redirects users who
// don't have access to the requested module to a safe page (Dashboard).
export const ModuleGuard = ({ module: moduleKey, children, redirectTo = "/" }) => {
  const { user } = useAuth();
  const location = useLocation();
  const denied = user && !hasModuleAccess(user, moduleKey);

  if (user === null) return null;
  if (denied) {
    return <Navigate to={redirectTo} replace state={{ from: location.pathname }} />;
  }
  return children;
};
